/**
 * UserCouponListContainer
 * --
 */
import React, { useState, useMemo } from 'react';
import MessageAlert from 'utils';
import UserCouponListPresenter from './UserCouponListPresenter';
import dayjs from 'dayjs';
import { useGetUsers, userQueryKeys } from 'hooks/UserHooks';
import { useGetCoupons, couponQueryKeys } from 'hooks/CouponHooks';
import {
  useBulkDeleteCouponUser,
  useBulkUpdateCouponUser,
  useGetCouponUsers,
  useBulkCreateCouponUser,
  couponUserQueryKeys,
} from 'hooks/CouponUserHooks';
import { useGetOrders } from 'hooks/OrderHooks';
import { useGetParcelServices } from 'hooks/ParcelServiceHooks';
import { useGetProductPolicies } from 'hooks/ProductPolicyHooks';
import Alert from 'utils/AlertManager';
import { useQueryClient } from '@tanstack/react-query';

const INITIAL_VALUE = {
  type: '',
  start_date: dayjs().subtract(1, 'months').startOf('day'),
  end_date: dayjs().endOf('day'),
  value: 30,
  searchKey: '',
  searchValue: '',
  update_date: dayjs(),
  issue_count: 0,
};
/**----------------------------------------
 * [Component] 총관리자: 회원관리 -> 쿠폰발급내역
 * ----------------------------------------
 */
const UserCouponListContainer = ({ permission }) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [currentCoupon, setCurrentCoupon] = useState(null);
  const [getOrderType, setGetOrderType] = useState(null);
  const [period, setPeriod] = useState({
    start_date: dayjs().subtract(1, 'months').startOf('day').unix(),
    end_date: dayjs().endOf('day').unix(),
  });
  const [inputValue, setInputValue] = React.useState(INITIAL_VALUE);
  const [selectedList, setSelectedList] = useState([]);
  const [buttonType, setButtonType] = React.useState(null);
  const [modalData, setModalData] = React.useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const confirm = (msg = '') => window.confirm(msg);

  /* ===== MUTATE ===== */
  const { data: CouponList } = useGetCoupons(null, {
    select: (res) => {
      const { status, data } = res;
      if (status !== 200) {
        return [];
      }

      return data;
    },
    initialData: [],
  });
  // 쿠폰 정책 조회
  const { data: CouponPolicy } = useGetProductPolicies(
    {
      policy_type: 'COUPON',
    },
    {
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        const filtered = data.filter((_item) => _item?.is_show === 'Y');

        return filtered;
      },
      initialData: [],
    }
  );
  const { data: UserList } = useGetUsers(null, {
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) {
        return [];
      }

      return data;
    },
    initialData: [],
  });

  const { data: CouponUserList } = useGetCouponUsers(
    {
      coupon_id: currentCoupon,
      start_date: period.start_date,
      end_date: period.end_date,
    },
    {
      select: (res) => {
        const { status, data } = res;
        if (status !== 200) {
          return [];
        }

        return data;
      },
      initialData: [],
      enabled: !!currentCoupon,
    }
  );
  const { data: OrderList } = useGetOrders(
    {
      user_id: currentUser,
      filter: 'coupon_info',
      join_tables: `["shop_information"]`,
    },
    {
      select: (res) => {
        const { status, data } = res;
        if (status !== 200) {
          return [];
        }

        return data;
      },
      initialData: [],
      enabled: !!currentUser && getOrderType === 'order',
    }
  );
  const { data: ParcelServiceList } = useGetParcelServices(
    {
      user_id: currentUser,
      filter: 'coupon_info',
      join_tables: `["shop_information"]`,
    },
    {
      select: (res) => {
        const { status, data } = res;
        if (status !== 200) {
          return [];
        }

        return data;
      },
      initialData: [],
      enabled: !!currentUser && getOrderType === 'parcel_service',
    }
  );
  const { bulkUpdateCouponUser } = useBulkUpdateCouponUser();
  const { bulkCreateCouponUser } = useBulkCreateCouponUser();
  const coupons = useMemo(() => {
    const newCouponList = CouponList?.filter((coupon) => {
      return ['MANUAL', 'DOWNLOAD'].includes(coupon?.payment_type);
    })?.map((_item) => ({
      ..._item,
      key: _item?.coupon_id,
      product_policy:
        CouponPolicy?.find((_sub) => _sub?.coupon_id === _item?.coupon_id) ??
        null,
    }));

    // 정책이 없는 쿠폰(발행 불가한 쿠폰) 필터
    const filterCouponList = newCouponList?.filter(
      (_item) => _item?.product_policy
    );

    return filterCouponList;
  }, [CouponList, CouponPolicy]);

  const { matchedCoupon, couponList } = useMemo(() => {
    const couponListMap = new Map(
      CouponList.map((coupon) => [coupon.coupon_id, coupon])
    );

    ////
    const newMenuCoupon = couponListMap?.get(currentCoupon);
    const groupedUsers = {};

    CouponUserList.toSorted(
      (a, b) => b?.coupon_user_id - a?.coupon_user_id
    )?.forEach((coupon_user) => {
      const foundUser = UserList?.find(
        (user) => user?.user_id === coupon_user?.user_id
      );

      const userKey = foundUser?.user_id;

      if (!groupedUsers[userKey]) {
        groupedUsers[userKey] = {
          key: userKey,
          ...foundUser,
          user_type: foundUser?.user_name ? 'USER' : 'NONE',
          route: foundUser?.social_type || 'MEMBER',
          coupon_user: [],
        };
      }
      groupedUsers[userKey].coupon_user.push({
        key: coupon_user?.coupon_user_id,
        ...coupon_user,
      });
    });

    const matchedCoupon = {
      key: newMenuCoupon?.coupon_id,
      ...newMenuCoupon,
      user: Object.values(groupedUsers),
    };

    return { matchedCoupon, couponList: CouponList };
  }, [CouponList, CouponUserList, currentCoupon]);

  const { orderList, shop_information_map } = useMemo(() => {
    const shopInformationMap = new Map();

    const newOrderList = OrderList?.filter((order) =>
      order?.coupon_info?.some((coupon) => coupon?.coupon_id === currentCoupon)
    )
      ?.map((order) => {
        shopInformationMap.set(
          order?.shop_information?.shop_information_id,
          order?.shop_information?.shop_information_name
        );
        return {
          ...order,
          product_information: order?.product_information?.map((menu) => {
            const total_option_price =
              menu?.menu_option?.reduce(
                (acc, options) =>
                  acc +
                  options?.option?.reduce(
                    (acc2, option) =>
                      acc2 + option?.option_price * (option?.count || 1),
                    0
                  ),
                0
              ) || 0;
            return {
              ...menu,
              total_option_price,
              total_price: (menu?.price + total_option_price) * menu?.num,
            };
          }),
        };
      })
      ?.toSorted((a, b) => b?.order_id - a?.order_id);
    const newParcelServiceList = ParcelServiceList?.filter(
      (parcel) => parcel?.coupon_info?.coupon_id === currentCoupon
    )
      ?.map((parcel) => {
        shopInformationMap.set(
          parcel?.shop_information?.shop_information_id,
          parcel?.shop_information?.shop_information_name
        );
        const total_option_price =
          parcel?.product?.product_option?.reduce(
            (acc, options) =>
              acc +
              options?.option?.reduce(
                (acc2, option) =>
                  acc2 + option?.option_price * (option?.count || 1),
                0
              ),
            0
          ) || 0;

        return {
          ...parcel,
          product: {
            ...parcel?.product,
            total_option_price,
            total_price:
              (parcel?.product?.price + total_option_price) *
              parcel?.product?.num,
          },
        };
      })
      ?.toSorted((a, b) => b?.parcel_service_id - a?.parcel_service_id);
    return {
      orderList: getOrderType === 'order' ? newOrderList : newParcelServiceList,
      shop_information_map: shopInformationMap,
    };
  }, [OrderList, ParcelServiceList, currentUser, currentCoupon]);

  const { bulkDeleteCouponUser } = useBulkDeleteCouponUser();
  /////--------
  /////--------
  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */

  const handleReload = () => {
    const queryKeys1 = userQueryKeys.getUsers({});
    const queryKeys2 = couponQueryKeys.getCoupons();
    const queryKeys3 = couponUserQueryKeys.getCouponUsers({
      coupon_id: currentCoupon,
      start_date: period.start_date,
      end_date: period.end_date,
    });
    queryClient.invalidateQueries({ queryKey: queryKeys1.queryKey });
    queryClient.invalidateQueries({ queryKey: queryKeys2.queryKey });
    queryClient.invalidateQueries({ queryKey: queryKeys3.queryKey });
  };
  /**
   *
   * @param {string} t
   * @param {string | number} v
   */
  const handleChange = (t, v) => {
    setInputValue((prev) => ({
      ...prev,
      [t]: v,
    }));
  };

  /**--------------------
   * 쿠폰 핸들러
   * --------------------
   * @param {object | array} data
   * @param {number} reserve
   */
  const handleCouponUserUpdate = async () => {
    setLoading(true);
    try {
      const msg =
        buttonType === 'use_date'
          ? `${selectedList?.length}건을 사용처리 하시겠습니까?`
          : `${selectedList?.length}건을 만료처리 하시겠습니까?`;

      if (!confirm(msg)) return;

      await bulkUpdateCouponUser(
        selectedList?.map((x) => ({
          coupon_user_id: x.coupon_user_id,
          [buttonType]: inputValue.update_date.unix(),
        }))
      );
      MessageAlert.success(
        `쿠폰 ${buttonType === 'use_date' ? '사용' : '만료'
        }처리 성공하였습니다!`
      );
      setModalData({
        ...modalData,
        coupon_user: modalData.coupon_user?.map((item) => {
          const findcoupon = selectedList?.find(
            (select) => select?.coupon_user_id === item.coupon_user_id
          );
          if (findcoupon) {
            return {
              ...item,
              [buttonType]: inputValue.update_date.unix(),
            };
          }
          return item;
        }),
      });
    } catch (err) {
      console.error('[handeleCouponUserUpdate] Error!! ', err);
      MessageAlert.error(
        `쿠폰 ${buttonType === 'USED' ? '사용' : '만료'}처리 성공하였습니다!`
      );
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  /**
   * 회원 선택 핸들러
   * --
   * @param {string} type
   * @param {object | array} obj
   */
  const handleSelect = (type, obj) => {
    const selectType = type.toLocaleUpperCase();
    switch (selectType) {
      case 'CHECKED': // 개별 선택
        setSelectedList((prev) => [...prev, obj]);
        return;
      case 'UNCHECKED': // 개별 선택 해제
        setSelectedList((prev) =>
          prev?.filter((select) => select?.key !== obj?.key)
        );
        return;
      case 'ALL-CHECKED': // 노출된 목록 전체 선택
        setSelectedList(obj);
        return;
      case 'ALL-UNCHECKED': // 노출된 목록 전체 선택 해제
        const removeList = selectedList?.filter((user) =>
          obj?.some((ob) => ob?.key === user?.key)
        );

        setSelectedList(removeList);

        return;
      case 'ALL': // 검색된 회원 전체 선택/해제
        const isAll = selectedList?.length === obj?.length;

        if (!isAll) {
          setSelectedList(obj);
        } else {
          setSelectedList([]);
        }
        return;
      default:
        return;
    }
  };

  /**
   * 쿠폰함 삭제
   * --
   * @param {Array} arr
   * @returns
   */
  const handleDeleteCouponUser = async (arr = []) => {
    const mutate = arr?.map((item) => ({
      coupon_user_id: item?.coupon_user_id,
    }));

    if (mutate.length >= 500) {
      MessageAlert.info('최대 500건까지 삭제 가능합니다.');
      return;
    }

    if (
      !Alert.confirm(
        '정말 발급한 쿠폰을 삭제하시겠습니까?\n삭제 시 복구 불가능하며 사용내역, 발급내역에서 모두 삭제됩니다.'
      )
    )
      return;

    try {
      setLoading(true);

      const { status, message } = await bulkDeleteCouponUser(mutate);

      if (status !== 200) {
        MessageAlert.warning('쿠폰 삭제에 실패하였습니다.');
        console.log('[handleDeleteCouponUser] Fail!! ', message);
        return;
      }

      MessageAlert.success(`쿠폰 삭제 성공하였습니다!`);

      handleReload();
      if (modalData) {
        setModalData((prev) => ({
          ...prev,
          coupon_user: modalData?.coupon_user.filter(
            (item) =>
              !mutate.some((d) => d.coupon_user_id === item.coupon_user_id)
          ),
        }));
      };

    } catch (err) {
      console.error('[handleDeleteCouponUser] Error!! ', err);
      MessageAlert.error(`쿠폰 삭제 실패하였습니다!`);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  const handleCouponRegister = async (issueData, callback) => {
    let isSuccess = false;
    const { user_ids, coupon_id, per_user_limit } = issueData;
    try {
      if (!issueData?.user_ids?.length)
        return MessageAlert.info('발급할 회원이 없습니다.');

      if (!issueData?.coupon_id)
        return MessageAlert.info('발급될 쿠폰 정보가 없습니다.');

      if (
        !confirm(
          `${issueData?.user_ids?.length}명에게 쿠폰을 ${issueData?.per_user_limit}장씩 발급 하시겠습니까?`
        )
      )
        return;

      setLoading(true);

      const newData = {
        user_ids: user_ids.map((user_id) => ({ user_id })),
        coupon_id,
        per_user_limit,
      };
      const { status, data } = await bulkCreateCouponUser(newData);
      if (status !== 200) {
        const koreanOnly =
          typeof data === 'string'
            ? data?.replace(/[^가-힣]/g, '')
            : '쿠폰 발급 실패하였습니다!';
        return MessageAlert.info(koreanOnly);
      }

      MessageAlert.success(
        `${issueData?.user_ids?.length} 명에게 쿠폰이 발급되었습니다.`
      );
      handleReload();
    } catch (err) {
      console.error('[handleCouponRegister] Error!! ', err);
      MessageAlert.error(`쿠폰 발급 실패하였습니다!`);
    } finally {
      setTimeout(() => {
        setLoading(false);
        isSuccess && callback();
      }, 1000);
    }
  };
  /* ===== HOOKS ===== */
  // /* 초기 설정 & 업체변경 시 반영 */
  /* ===== RENDER ===== */
  return (
    <UserCouponListPresenter
      {...{
        /* States */
        coupons,
        couponList,
        couponUserList: CouponUserList,
        orderList: orderList,
        coupon: matchedCoupon,
        shop_information_map: shop_information_map,
        permission,
        loading,
        setCurrentCoupon,
        currentCoupon,
        setCurrentUser,
        getOrderType,
        setGetOrderType,
        period,
        setPeriod,
        onChange: handleChange,
        inputValue: inputValue,
        setInputValue: setInputValue,
        /* Events */
        setLoading,

        buttonType,
        setButtonType,
        onSelect: handleSelect,
        INITIAL_VALUE,
        onHandleCouponUserUpdate: handleCouponUserUpdate,
        modalData,
        setModalData,
        selectedList,
        setSelectedList,
        onDelete: handleDeleteCouponUser,
        onCouponRegister: handleCouponRegister,
      }}
    />
  );
};

/* DF */
UserCouponListContainer.defaultProps = {
  defaultShopId: null,
  permission: 'ADMIN',
};
export default UserCouponListContainer;
